<template>
  <div class="energy-overview-page">
    <div class="page-top-bar">
      <div class="page-top-left">
        <h2 class="page-title">数据概览</h2>
        <span class="update-time">
          <i class="el-icon-time"></i>
          最后更新：{{ lastUpdated }}
        </span>
      </div>
    </div>

    <div class="stat-cards-row">
      <stat-card
        v-for="card in summaryCards"
        :key="card.key"
        v-bind="card"
      />
    </div>

    <div class="main-section">
      <div class="scene-area">
        <div class="scene-bg" :style="{ backgroundImage: `url(${sceneBg})` }"></div>
        <div class="scene-overlay"></div>
        <div class="scene-grid"></div>

        <div class="scene-content">
          <div
            v-for="(node, index) in sceneNodes"
            :key="'node-' + index"
            class="scene-node"
            :style="{ top: node.top, left: node.left }"
          >
            <div class="node-icon-wrap">
              <i :class="node.icon"></i>
            </div>
            <span class="node-name">{{ node.name }}</span>
          </div>

          <div class="scene-center">
            <div class="center-ring"></div>
            <div class="center-core">
              <i class="el-icon-s-platform"></i>
              <span>能碳一体机</span>
            </div>
          </div>

          <div
            v-for="(label, index) in sceneLabels"
            :key="'label-' + index"
            class="scene-label"
            :class="{ 'label-right': label.align === 'right' }"
            :style="{ top: label.top, left: label.left, right: label.right }"
          >
            <div class="label-dot"></div>
            <div class="label-body">
              <div class="label-name">{{ label.label }}</div>
              <div class="label-value">{{ label.value }}</div>
            </div>
          </div>
        </div>

        <div class="monitor-card">
          <div class="monitor-header">
            <i class="el-icon-monitor"></i>
            监控终端设备实时数据
          </div>
          <el-select v-model="monitorLocation" size="mini" class="monitor-select">
            <el-option
              v-for="loc in realtimeMonitor.locations"
              :key="loc"
              :label="loc"
              :value="loc"
            />
          </el-select>
          <div class="monitor-stats">
            <span class="stat-item">
              <em>总数</em>
              <strong>{{ realtimeMonitor.total }}</strong>
            </span>
            <span class="stat-item text-success">
              <em>在线</em>
              <strong>{{ realtimeMonitor.online }}</strong>
            </span>
            <span class="stat-item text-danger">
              <em>离线</em>
              <strong>{{ realtimeMonitor.offline }}</strong>
            </span>
          </div>
          <div class="monitor-value">
            <span class="value-num">{{ formatNumber(realtimeMonitor.currentValue, 2) }}</span>
            <span class="value-unit">kWh</span>
          </div>
          <div class="mini-chart">
            <base-chart :option="miniChartOption" />
          </div>
        </div>
      </div>

      <div class="right-panel">
        <chart-card title="今日用能曲线">
          <template slot="extra">
            <el-select v-model="energyType" size="mini" class="energy-type-select">
              <el-option label="电" value="electricity" />
              <el-option label="水" value="water" />
            </el-select>
          </template>
          <base-chart :option="energyCurveOption" />
        </chart-card>

        <chart-card title="用能策略关键指标监测" class="strategy-card">
          <div class="strategy-list">
            <div
              v-for="item in strategyList"
              :key="item.id"
              class="strategy-item"
            >
              <div class="strategy-index">{{ String(item.id).padStart(2, '0') }}</div>
              <div class="strategy-content">
                <div class="strategy-title">{{ item.title }}</div>
                <div class="strategy-desc">{{ item.desc }}</div>
                <div class="strategy-time">
                  <i class="el-icon-time"></i>
                  {{ item.time }}
                </div>
              </div>
            </div>
          </div>
        </chart-card>
      </div>
    </div>
  </div>
</template>

<script>
import StatCard from '@/components/common/StatCard.vue'
import ChartCard from '@/components/common/ChartCard.vue'
import BaseChart from '@/components/charts/BaseChart.vue'
import { getOverviewData } from '@/api/modules/energyQuery'
import { formatNumber } from '@/utils/format'
import sceneBg from '@/assets/images/overview-scene-bg.jpg'

export default {
  name: 'EnergyQueryOverview',
  components: { StatCard, ChartCard, BaseChart },
  data() {
    return {
      sceneBg,
      summaryCards: [],
      lastUpdated: '',
      sceneNodes: [],
      sceneLabels: [],
      realtimeMonitor: {
        locations: [],
        total: 0,
        online: 0,
        offline: 0,
        currentValue: 0,
        trend: []
      },
      monitorLocation: '',
      strategyList: [],
      energyType: 'electricity',
      miniChartOption: {},
      energyCurveOption: {}
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    formatNumber,
    async loadData() {
      const res = await getOverviewData()
      const data = res.data
      this.summaryCards = data.summaryCards
      this.lastUpdated = data.lastUpdated
      this.sceneNodes = data.sceneNodes
      this.sceneLabels = data.sceneLabels
      this.realtimeMonitor = data.realtimeMonitor
      this.monitorLocation = data.realtimeMonitor.currentLocation
      this.strategyList = data.strategyList
      this.buildMiniChart(data.realtimeMonitor.trend)
      this.buildEnergyCurve(data.energyCurve)
    },
    buildMiniChart(trend) {
      this.miniChartOption = {
        grid: { left: 4, right: 4, top: 8, bottom: 4 },
        xAxis: { type: 'category', show: false, data: trend.map((_, i) => i) },
        yAxis: { type: 'value', show: false },
        series: [{
          type: 'line',
          data: trend,
          smooth: true,
          symbol: 'none',
          lineStyle: { color: '#409EFF', width: 2 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(64, 158, 255, 0.35)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.02)' }
              ]
            }
          }
        }]
      }
    },
    buildEnergyCurve(curve) {
      this.energyCurveOption = {
        tooltip: {
          trigger: 'axis',
          backgroundColor: 'rgba(255,255,255,0.96)',
          borderColor: '#e4e7ed',
          textStyle: { color: '#303133', fontSize: 12 }
        },
        grid: { left: 44, right: 16, top: 28, bottom: 28 },
        xAxis: {
          type: 'category',
          data: curve.times,
          axisLine: { lineStyle: { color: '#e4e7ed' } },
          axisTick: { show: false },
          axisLabel: { color: '#909399', fontSize: 11 }
        },
        yAxis: {
          type: 'value',
          name: 'kWh',
          nameTextStyle: { color: '#909399', fontSize: 11 },
          axisLine: { show: false },
          axisTick: { show: false },
          splitLine: { lineStyle: { color: '#f0f2f5', type: 'dashed' } },
          axisLabel: { color: '#909399', fontSize: 11 }
        },
        series: [{
          type: 'line',
          data: curve.values,
          smooth: true,
          symbol: 'circle',
          symbolSize: 6,
          showSymbol: false,
          itemStyle: { color: '#409EFF', borderColor: '#fff', borderWidth: 2 },
          lineStyle: { width: 2.5, shadowColor: 'rgba(64,158,255,0.3)', shadowBlur: 8 },
          areaStyle: {
            color: {
              type: 'linear',
              x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(64, 158, 255, 0.25)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.02)' }
              ]
            }
          }
        }]
      }
    }
  }
}
</script>

<style lang="less" scoped>
@import '~@/styles/variables.less';

.energy-overview-page {
  min-height: 100%;
}

.page-top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}

.page-top-left {
  display: flex;
  align-items: baseline;
  gap: 16px;
}

.page-title {
  font-size: 18px;
  font-weight: 600;
  color: @text-primary;
  margin: 0;
  letter-spacing: 0.02em;
}

.update-time {
  font-size: 12px;
  color: @text-secondary;
  display: flex;
  align-items: center;
  gap: 4px;

  i {
    font-size: 13px;
  }
}

.main-section {
  display: flex;
  gap: 16px;
  height: calc(100vh - @header-height - 200px);
  min-height: 520px;
}

.scene-area {
  flex: 1;
  position: relative;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(26, 58, 92, 0.08), 0 8px 32px rgba(26, 58, 92, 0.06);
  overflow: hidden;
  border: 1px solid rgba(64, 158, 255, 0.12);
}

.scene-bg {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  transform: scale(1.02);
}

.scene-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(
    135deg,
    rgba(26, 58, 92, 0.82) 0%,
    rgba(26, 58, 92, 0.55) 35%,
    rgba(64, 158, 255, 0.35) 65%,
    rgba(232, 244, 253, 0.45) 100%
  );
}

.scene-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 48px 48px;
  pointer-events: none;
}

.scene-content {
  position: absolute;
  inset: 0;
  z-index: 1;
}

.scene-node {
  position: absolute;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  z-index: 2;

  .node-icon-wrap {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.92);
    backdrop-filter: blur(8px);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    color: @active-color;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.6);
    transition: transform 0.2s ease;

    &:hover {
      transform: scale(1.08);
    }
  }

  .node-name {
    font-size: 11px;
    color: rgba(255, 255, 255, 0.9);
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
    font-weight: 500;
    white-space: nowrap;
  }
}

.scene-center {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 3;

  .center-ring {
    position: absolute;
    top: 50%;
    left: 50%;
    width: 140px;
    height: 140px;
    transform: translate(-50%, -50%);
    border: 2px dashed rgba(64, 158, 255, 0.4);
    border-radius: 50%;
    animation: rotate 20s linear infinite;
  }

  .center-core {
    position: relative;
    width: 100px;
    height: 100px;
    border-radius: 50%;
    background: linear-gradient(145deg, rgba(255, 255, 255, 0.95), rgba(232, 244, 253, 0.9));
    box-shadow: 0 8px 32px rgba(64, 158, 255, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.8);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 4px;
    color: @primary-color;
    font-size: 13px;
    font-weight: 600;

    i {
      font-size: 32px;
      color: @active-color;
    }
  }
}

@keyframes rotate {
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
}

.scene-label {
  position: absolute;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  z-index: 4;
  transform: translate(-50%, -50%);

  &.label-right {
    flex-direction: row-reverse;
    transform: translate(50%, -50%);
  }

  .label-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: @active-color;
    box-shadow: 0 0 8px rgba(64, 158, 255, 0.8);
    margin-top: 10px;
    flex-shrink: 0;
    animation: pulse 2s ease-in-out infinite;
  }

  .label-body {
    background: rgba(255, 255, 255, 0.94);
    backdrop-filter: blur(12px);
    border-radius: 8px;
    padding: 8px 14px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.5);
    min-width: 110px;
  }

  .label-name {
    font-size: 11px;
    color: @text-secondary;
    margin-bottom: 2px;
  }

  .label-value {
    font-size: 13px;
    font-weight: 700;
    color: @text-primary;
    letter-spacing: -0.01em;
  }
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.6; transform: scale(1.2); }
}

.monitor-card {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 280px;
  background: rgba(255, 255, 255, 0.94);
  backdrop-filter: blur(16px);
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(255, 255, 255, 0.6);
  padding: 16px;
  z-index: 10;

  .monitor-header {
    font-size: 13px;
    font-weight: 600;
    color: @text-primary;
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    gap: 6px;

    i {
      color: @active-color;
      font-size: 15px;
    }
  }

  .monitor-select {
    width: 100%;
    margin-bottom: 10px;
  }

  .monitor-stats {
    display: flex;
    gap: 8px;
    margin-bottom: 10px;

    .stat-item {
      flex: 1;
      background: #f5f7fa;
      border-radius: 6px;
      padding: 6px 8px;
      text-align: center;

      em {
        display: block;
        font-size: 11px;
        color: @text-secondary;
        font-style: normal;
        margin-bottom: 2px;
      }

      strong {
        font-size: 16px;
        font-weight: 700;
        color: @text-primary;
      }
    }
  }

  .monitor-value {
    margin-bottom: 8px;

    .value-num {
      font-size: 26px;
      font-weight: 700;
      color: @primary-color;
      letter-spacing: -0.02em;
    }

    .value-unit {
      font-size: 13px;
      color: @text-secondary;
      margin-left: 4px;
    }
  }

  .mini-chart {
    height: 64px;
    margin: 0 -4px;
  }
}

.right-panel {
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;

  .chart-card:first-child {
    height: 260px;
  }

  .strategy-card {
    flex: 1;
    overflow: hidden;
  }
}

.energy-type-select {
  width: 88px;
}

.strategy-list {
  overflow-y: auto;
  max-height: 100%;
  padding-right: 4px;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #dcdfe6;
    border-radius: 2px;
  }
}

.strategy-item {
  display: flex;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #f0f2f5;
  transition: background 0.15s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: #fafbfc;
    margin: 0 -8px;
    padding-left: 8px;
    padding-right: 8px;
    border-radius: 6px;
  }

  .strategy-index {
    font-size: 22px;
    font-weight: 800;
    color: @active-color;
    opacity: 0.35;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }

  .strategy-title {
    font-size: 14px;
    font-weight: 600;
    color: @text-primary;
    margin-bottom: 4px;
  }

  .strategy-desc {
    font-size: 12px;
    color: @text-secondary;
    line-height: 1.6;
  }

  .strategy-time {
    font-size: 11px;
    color: #c0c4cc;
    margin-top: 6px;
    display: flex;
    align-items: center;
    gap: 4px;
  }
}

@media (max-width: 1400px) {
  .stat-cards-row {
    grid-template-columns: repeat(3, 1fr) !important;
  }

  .right-panel {
    width: 340px;
  }
}
</style>
